import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = ["General Contracting", "Roofing & HVAC", "Plumbing & Electrical", "Junk Removal", "Pressure Washing", "Landscaping", "Painting", "Concrete", "Tree Service", "Luxury Renovation", "Other Trade"];
const budgets = ["Starter — $299 + $79/mo", "Growth — $499 + $149/mo", "Premium — $899 + $249/mo", "Not sure yet"];
const featureOptions = ["Project Gallery", "Quote Intake Form", "Online Booking", "Local SEO", "Before/After Photos", "Reviews Integration", "Service Area Pages", "Blog / Content"];

export default function Contact() {
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState("");
  const [budget, setBudget] = useState("");
  const [features, setFeatures] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const toggleFeature = (f) => setFeatures((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const canNext = [industry !== "", budget !== "", true, form.name !== "" && form.email !== ""][step];

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { industry, budget, features, ...form });
      setDone(true);
      toast.success("Proposal request received. We'll reach out within one business day.");
    } catch {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const steps = ["Your Trade", "Your Budget", "Features", "Contact"];

  return (
    <section id="contact" data-testid="agency-contact-section" className="py-28 lg:py-36 bg-surface/30 border-t gold-border-subtle">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">Start a Project</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8F6F0] tracking-tight max-w-2xl">
            Let's build your <span className="gold-text italic">next website.</span>
          </h2>
        </motion.div>

        <div className="rounded-3xl border gold-border-subtle bg-obsidian/60 p-8 sm:p-12">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-14 text-center" data-testid="contact-success">
              <CheckCircle2 size={52} className="text-gold mx-auto mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl text-[#F8F6F0] mb-3">Request received.</h3>
              <p className="text-zinc-400 max-w-md mx-auto leading-relaxed">
                We'll review your trade and budget, then send a tailored proposal within one business day.
              </p>
            </motion.div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-10" data-testid="contact-step-indicator">
                {steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-zinc-800"}`} />
                  </div>
                ))}
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 ml-3">
                  {step + 1} / {steps.length} — {steps[step]}
                </span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                >
                  {step === 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="contact-industry-grid">
                      {industries.map((ind) => (
                        <button
                          key={ind}
                          data-testid={`industry-${ind.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => setIndustry(ind)}
                          className={`px-4 py-3.5 rounded-xl border font-sans text-sm transition-all duration-300 ${
                            industry === ind ? "border-gold bg-gold/10 text-gold" : "border-zinc-800 text-zinc-400 hover:border-gold/40 hover:text-zinc-200"
                          }`}
                        >
                          {ind}
                        </button>
                      ))}
                    </div>
                  )}
                  {step === 1 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-budget-grid">
                      {budgets.map((b) => (
                        <button
                          key={b}
                          data-testid={`budget-${b.split(" ")[0].toLowerCase()}`}
                          onClick={() => setBudget(b)}
                          className={`px-5 py-4 rounded-xl border font-sans text-sm text-left transition-all duration-300 ${
                            budget === b ? "border-gold bg-gold/10 text-gold" : "border-zinc-800 text-zinc-400 hover:border-gold/40 hover:text-zinc-200"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  )}
                  {step === 2 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="contact-features-grid">
                      {featureOptions.map((f) => (
                        <button
                          key={f}
                          data-testid={`feature-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => toggleFeature(f)}
                          className={`px-4 py-3.5 rounded-xl border font-sans text-sm transition-all duration-300 ${
                            features.includes(f) ? "border-gold bg-gold/10 text-gold" : "border-zinc-800 text-zinc-400 hover:border-gold/40 hover:text-zinc-200"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                  {step === 3 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-details-form">
                      <input
                        data-testid="contact-name-input"
                        placeholder="Your name *"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="px-5 py-4 rounded-xl bg-surface border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
                      />
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        placeholder="Email *"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="px-5 py-4 rounded-xl bg-surface border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
                      />
                      <input
                        data-testid="contact-phone-input"
                        placeholder="Phone (optional)"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="px-5 py-4 rounded-xl bg-surface border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
                      />
                      <input
                        data-testid="contact-message-input"
                        placeholder="Anything we should know? (optional)"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="px-5 py-4 rounded-xl bg-surface border border-zinc-800 text-zinc-200 placeholder-zinc-600 focus:border-gold/60 focus:outline-none transition-colors"
                      />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                <button
                  data-testid="contact-back-btn"
                  onClick={() => setStep(step - 1)}
                  disabled={step === 0}
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-gold transition-colors disabled:opacity-0"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                {step < 3 ? (
                  <button
                    data-testid="contact-next-btn"
                    onClick={() => canNext && setStep(step + 1)}
                    className={`group flex items-center gap-2 px-7 py-3.5 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                      canNext ? "bg-gold text-obsidian hover:bg-gold-hover" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                    }`}
                  >
                    Continue <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ) : (
                  <button
                    data-testid="contact-submit-btn"
                    onClick={submit}
                    disabled={!canNext || sending}
                    className={`flex items-center gap-2 px-7 py-3.5 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                      canNext && !sending ? "bg-gold text-obsidian hover:bg-gold-hover hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                    }`}
                  >
                    {sending ? "Sending..." : "Request Proposal"} <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
